import { DeleteOutlined } from '@mui/icons-material';
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import type { StyleObj } from '../../@types';
import type { CashbackBonusFormValues } from '../../schema';
import FormNumberInput from '../molecules/FormNumberInput';

export type CashbackTierProps = {
  index: number;
  disabled: boolean;
  onRemove: () => void;
  showRemove: boolean;
};

type Props = CashbackTierProps & { children: ReactNode };

const styles: StyleObj = {
  container: { p: 2 },
  header: { alignItems: 'center', justifyContent: 'space-between' },
  fields: { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } },
};

const CashbackTierFields = ({ index, disabled, onRemove, showRemove, children }: Props) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<CashbackBonusFormValues>();
  const tierErrors = errors.freeSpinTiers?.[index];

  return (
    <Paper variant='outlined' sx={styles.container}>
      <Stack spacing={2}>
        <Stack direction='row' sx={styles.header}>
          <Typography variant='subtitle2'>Tier {index + 1}</Typography>
          {!disabled && showRemove && (
            <IconButton aria-label={`Remove tier ${index + 1}`} color='error' onClick={onRemove} size='small'>
              <DeleteOutlined />
            </IconButton>
          )}
        </Stack>
        <Box sx={styles.fields}>
          <FormNumberInput
            control={control}
            name={`freeSpinTiers.${index}.minLossAmount`}
            label='Min Loss Amount'
            error={tierErrors?.minLossAmount}
            allowDecimals
            decimalPlaces={2}
            valueAsString
            displayCurrency
            disabled={disabled}
            required
          />
          <FormNumberInput
            control={control}
            name={`freeSpinTiers.${index}.maxLossAmount`}
            label='Max Loss Amount'
            error={tierErrors?.maxLossAmount}
            allowDecimals
            decimalPlaces={2}
            valueAsString
            displayCurrency
            disabled={disabled}
            placeholder='Unlimited'
          />
          <FormNumberInput
            control={control}
            name={`freeSpinTiers.${index}.freeSpinsAwarded`}
            label='Free Spins Awarded'
            error={tierErrors?.freeSpinsAwarded}
            disabled={disabled}
            required
          />
          {children}
        </Box>
      </Stack>
    </Paper>
  );
};

export default CashbackTierFields;
